import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

@Directive({
    selector: 'ng2-pdfjs-viewer[mcPdfZoom]',
    standalone: false,
})
export class PdfViewerZoomDirective implements AfterViewInit, OnDestroy
{
    private destroyed = false;
    private attached = false;
    private cleanupFns: Array<() => void> = [];
    private attachAttempts = 0;

    constructor(
        private host: ElementRef<HTMLElement>,
        private zone: NgZone,
    ) {}

    ngAfterViewInit(): void
    {
        this.zone.runOutsideAngular(() =>
        {
            this.tryAttach();
        });
    }

    ngOnDestroy(): void
    {
        this.destroyed = true;
        for (const fn of this.cleanupFns)
        {
            try { fn(); } catch { }
        }
        this.cleanupFns = [];
    }

    private tryAttach(): void
    {
        if (this.destroyed || this.attached)
        {
            return;
        }

        const iframe = this.host.nativeElement.querySelector('iframe') as HTMLIFrameElement | null;
        if (!iframe)
        {
            this.retry(() => this.tryAttach());
            return;
        }

        const onLoad = () => this.attachInsideIframe(iframe);
        iframe.addEventListener('load', onLoad);
        this.cleanupFns.push(() => iframe.removeEventListener('load', onLoad));

        // If already loaded, attach immediately.
        try
        {
            const state = iframe.contentDocument?.readyState;
            if (state === 'complete' || state === 'interactive')
            {
                this.attachInsideIframe(iframe);
            }
        }
        catch
        {
            // Ignore cross-document timing issues and retry.
            this.retry(() => this.attachInsideIframe(iframe));
        }
    }

    private attachInsideIframe(iframe: HTMLIFrameElement): void
    {
        if (this.destroyed || this.attached)
        {
            return;
        }

        let win: any;
        let doc: Document | null | undefined;
        try
        {
            win = iframe.contentWindow as any;
            doc = iframe.contentDocument;
        }
        catch
        {
            this.retry(() => this.attachInsideIframe(iframe));
            return;
        }

        if (!win || !doc)
        {
            this.retry(() => this.attachInsideIframe(iframe));
            return;
        }

        const pdfApp = win.PDFViewerApplication;
        const pdfViewer = pdfApp?.pdfViewer;
        if (!pdfViewer)
        {
            this.retry(() => this.attachInsideIframe(iframe));
            return;
        }

        const viewerContainer: HTMLElement | null =
            (pdfViewer.container as HTMLElement | undefined) ??
            (doc.getElementById('viewerContainer') as HTMLElement | null) ??
            doc.body;

        const viewerElement: HTMLElement | null =
            (doc.getElementById('viewer') as HTMLElement | null) ??
            (doc.querySelector('.pdfViewer') as HTMLElement | null);

        if (!viewerContainer)
        {
            this.retry(() => this.attachInsideIframe(iframe));
            return;
        }

        const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

        // Keep the user's chosen zoom level instead of snapping back.
        // Still apply a wide safety range to avoid invalid / extreme values.
        const minScale = 0.1;
        const maxScale = 20;

        const getScale = (): number =>
        {
            const scale = Number(pdfViewer.currentScale);
            return Number.isFinite(scale) && scale > 0 ? scale : 1;
        };

        const setScale = (next: number): void =>
        {
            const clamped = clamp(next, minScale, maxScale);
            try
            {
                pdfViewer.currentScale = clamped;
            }
            catch
            {
                // Best-effort only.
            }
        };

        const zoomSteps = [1, 1.5, 2, 3];
        const getNextStepScale = (current: number): number =>
        {
            const epsilon = 0.05;
            for (const step of zoomSteps)
            {
                if (step > current + epsilon)
                {
                    return step;
                }
            }

            return zoomSteps[0];
        };

        const clampScrollLeft = (container: HTMLElement, next: number): number =>
        {
            const max = Math.max(0, container.scrollWidth - container.clientWidth);
            return clamp(next, 0, max);
        };

        const clampScrollTop = (container: HTMLElement, next: number): number =>
        {
            const max = Math.max(0, container.scrollHeight - container.clientHeight);
            return clamp(next, 0, max);
        };

        const zoomTowardsClientPoint = (nextScale: number, clientX: number, clientY: number): void =>
        {
            const currentScale = getScale();
            if (!Number.isFinite(nextScale) || nextScale <= 0)
            {
                return;
            }

            const rect = viewerContainer.getBoundingClientRect();
            const xInContainer = clientX - rect.left;
            const yInContainer = clientY - rect.top;

            // Current content coordinate under the tap/click.
            const contentX = viewerContainer.scrollLeft + xInContainer;
            const contentY = viewerContainer.scrollTop + yInContainer;

            setScale(nextScale);

            const ratio = nextScale / currentScale;
            const applyScroll = () =>
            {
                viewerContainer.scrollLeft = clampScrollLeft(viewerContainer, contentX * ratio - xInContainer);
                viewerContainer.scrollTop = clampScrollTop(viewerContainer, contentY * ratio - yInContainer);
            };

            // Wait for PDF.js to apply scale + relayout.
            requestAnimationFrame(() => requestAnimationFrame(applyScroll));
        };

        const onDblClick = (event: MouseEvent) =>
        {
            event.preventDefault();
            const current = getScale();
            const next = getNextStepScale(current);
            zoomTowardsClientPoint(next, event.clientX, event.clientY);
        };

        viewerContainer.addEventListener('dblclick', onDblClick, { passive: false });
        this.cleanupFns.push(() => viewerContainer.removeEventListener('dblclick', onDblClick as any));

        let lastTapAt = 0;
        let lastTapX = 0;
        let lastTapY = 0;

        let pinchActive = false;
        let pinchStartDistance = 0;
        let pinchStartScale = 1;
        let pinchTargetScale = 1;
        let pinchOriginX = 0;
        let pinchOriginY = 0;
        let pinchHadGesture = false;

        let rafId: number | null = null;
        let pendingTransformScale = 1;

        const cancelRaf = (): void =>
        {
            if (rafId !== null)
            {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        };

        const applyPinchTransform = (): void =>
        {
            rafId = null;
            if (!pinchActive || !viewerElement)
            {
                return;
            }

            viewerElement.style.transformOrigin = `${pinchOriginX}px ${pinchOriginY}px`;
            viewerElement.style.transform = `scale(${pendingTransformScale})`;
        };

        const distance = (t1: Touch, t2: Touch): number =>
        {
            const dx = t1.clientX - t2.clientX;
            const dy = t1.clientY - t2.clientY;
            return Math.hypot(dx, dy);
        };

        const onTouchStart = (event: TouchEvent) =>
        {
            if (event.touches.length === 2)
            {
                // Stop the browser/page from interpreting this as a native zoom.
                event.preventDefault();

                pinchActive = true;
                pinchHadGesture = true;
                pinchStartDistance = distance(event.touches[0], event.touches[1]);
                pinchStartScale = getScale();
                pinchTargetScale = pinchStartScale;

                if (viewerElement)
                {
                    // Anchor the pinch to the midpoint of the two touches, in content coordinates.
                    const rect = viewerContainer.getBoundingClientRect();
                    const centerX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
                    const centerY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
                    const xInContainer = centerX - rect.left;
                    const yInContainer = centerY - rect.top;

                    pinchOriginX = viewerContainer.scrollLeft + xInContainer;
                    pinchOriginY = viewerContainer.scrollTop + yInContainer;

                    viewerElement.style.willChange = 'transform';
                    viewerElement.style.transformOrigin = `${pinchOriginX}px ${pinchOriginY}px`;
                    viewerElement.style.transform = 'scale(1)';
                }
            }
        };

        const onTouchMove = (event: TouchEvent) =>
        {
            if (!pinchActive || event.touches.length !== 2)
            {
                return;
            }

            const d = distance(event.touches[0], event.touches[1]);
            if (pinchStartDistance > 0)
            {
                const nextScale = pinchStartScale * (d / pinchStartDistance);
                // Avoid snapping: preserve the user's chosen scale, only guard against invalid/extreme values.
                if (Number.isFinite(nextScale) && nextScale > 0)
                {
                    pinchTargetScale = clamp(nextScale, minScale, maxScale);
                }

                // Smooth path: apply a temporary CSS transform during the gesture.
                // Then commit to the real PDF.js zoom once on pinch end.
                if (viewerElement)
                {
                    // Update origin as the pinch midpoint moves.
                    const rect = viewerContainer.getBoundingClientRect();
                    const centerX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
                    const centerY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
                    const xInContainer = centerX - rect.left;
                    const yInContainer = centerY - rect.top;
                    pinchOriginX = viewerContainer.scrollLeft + xInContainer;
                    pinchOriginY = viewerContainer.scrollTop + yInContainer;

                    pendingTransformScale = pinchTargetScale / pinchStartScale;
                    if (rafId === null)
                    {
                        rafId = requestAnimationFrame(applyPinchTransform);
                    }
                }
                else
                {
                    // Fallback: no viewer element found, best-effort via PDF.js.
                    setScale(pinchTargetScale);
                }
            }

            // Prevent the browser from interpreting this as a page zoom.
            event.preventDefault();
        };

        const onTouchEnd = (event: TouchEvent) =>
        {
            if (pinchActive && event.touches.length < 2)
            {
                pinchActive = false;
                pinchStartDistance = 0;

                cancelRaf();
                if (viewerElement)
                {
                    viewerElement.style.transform = '';
                    viewerElement.style.transformOrigin = '';
                    viewerElement.style.willChange = '';
                }

                // Commit the final zoom once (avoids re-rendering on every move).
                if (pinchHadGesture)
                {
                    setScale(pinchTargetScale);
                }
                return;
            }

            if (pinchActive)
            {
                return;
            }

            // Double tap
            if (event.touches.length === 0 && event.changedTouches.length > 0)
            {
                const touch = event.changedTouches[0];
                const now = Date.now();

                // Ignore taps that are part of / immediately after a pinch.
                if (pinchHadGesture)
                {
                    pinchHadGesture = false;
                    lastTapAt = 0;
                    return;
                }

                const dx = touch.clientX - lastTapX;
                const dy = touch.clientY - lastTapY;
                const closeEnough = Math.hypot(dx, dy) < 30;

                if (now - lastTapAt < 300 && closeEnough)
                {
                    event.preventDefault();
                    const current = getScale();
                    const next = getNextStepScale(current);
                    zoomTowardsClientPoint(next, touch.clientX, touch.clientY);
                    lastTapAt = 0;
                }
                else
                {
                    lastTapAt = now;
                    lastTapX = touch.clientX;
                    lastTapY = touch.clientY;
                }
            }
        };

        const onTouchCancel = () =>
        {
            pinchActive = false;
            pinchStartDistance = 0;
            cancelRaf();

            if (viewerElement)
            {
                viewerElement.style.transform = '';
                viewerElement.style.transformOrigin = '';
                viewerElement.style.willChange = '';
            }
        };

        // Needs passive: false so we can prevent native pinch-to-zoom.
        viewerContainer.addEventListener('touchstart', onTouchStart, { passive: false });
        viewerContainer.addEventListener('touchmove', onTouchMove, { passive: false });
        viewerContainer.addEventListener('touchend', onTouchEnd, { passive: false });
        viewerContainer.addEventListener('touchcancel', onTouchCancel, { passive: true });

        this.cleanupFns.push(() => viewerContainer.removeEventListener('touchstart', onTouchStart as any));
        this.cleanupFns.push(() => viewerContainer.removeEventListener('touchmove', onTouchMove as any));
        this.cleanupFns.push(() => viewerContainer.removeEventListener('touchend', onTouchEnd as any));
        this.cleanupFns.push(() => viewerContainer.removeEventListener('touchcancel', onTouchCancel as any));

        this.attached = true;
    }

    private retry(fn: () => void): void
    {
        if (this.destroyed || this.attached)
        {
            return;
        }

        // Back off quickly so we don't spin if PDF.js isn't ready yet.
        if (this.attachAttempts++ > 60)
        {
            return;
        }

        setTimeout(fn, 100);
    }
}
